// =============================================================
// Global chrome — toasts + confirm dialog (ports window.PERP_TOAST /
// window.PERP_CONFIRM from the prototype into idiomatic React context, so any
// component can `useChrome().toast(...)` / `await confirm(...)`).
// =============================================================

import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

export type ToastKind = "success" | "warning" | "error" | "info";

interface ToastItem {
  id: number;
  text: string;
  kind: ToastKind;
}

interface ConfirmOpts {
  title?: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

interface Chrome {
  toasts: ToastItem[];
  toast: (text: string, kind?: ToastKind) => void;
  dismissToast: (id: number) => void;
  confirm: (opts: ConfirmOpts) => Promise<boolean>;
  pendingConfirm: (ConfirmOpts & { resolve: (ok: boolean) => void }) | null;
}

const ChromeCtx = createContext<Chrome | null>(null);

const TOAST_TTL_MS = 3400;

export function ChromeProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [pendingConfirm, setPendingConfirm] = useState<Chrome["pendingConfirm"]>(null);
  const seq = useRef(0);

  const dismissToast = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (text: string, kind: ToastKind = "success") => {
      const id = ++seq.current;
      setToasts((list) => [...list, { id, text, kind }]);
      setTimeout(() => dismissToast(id), TOAST_TTL_MS);
    },
    [dismissToast],
  );

  const confirm = useCallback(
    (opts: ConfirmOpts) =>
      new Promise<boolean>((resolve) => {
        setPendingConfirm({
          ...opts,
          resolve: (ok) => {
            setPendingConfirm(null);
            resolve(ok);
          },
        });
      }),
    [],
  );

  const value = useMemo<Chrome>(
    () => ({ toasts, toast, dismissToast, confirm, pendingConfirm }),
    [toasts, toast, dismissToast, confirm, pendingConfirm],
  );

  return <ChromeCtx.Provider value={value}>{children}</ChromeCtx.Provider>;
}

export function useChrome(): Chrome {
  const ctx = useContext(ChromeCtx);
  if (!ctx) throw new Error("useChrome must be used within ChromeProvider");
  return ctx;
}
